import type { LevelUpHeroCommand, MatchState } from "../../../../schemas"
import { HERO_LEVEL_UP_COSTS } from "../../../../schemas"
import type { CommandResult, HeroLookup } from "../state"
import {
  appendLog,
  buildCombatStateForHeroLevel,
  err,
  getSeat,
  updateInstance,
  updateSeat,
} from "../state"

export function handleLevelUpHero(
  state: MatchState,
  cmd: LevelUpHeroCommand,
  heroes: HeroLookup,
): CommandResult {
  const seat = getSeat(state, cmd.seatId)
  const { targetLevel } = cmd.payload

  const currentLevel = seat.hero.currentLevel
  if (targetLevel !== currentLevel + 1) {
    return err(
      "LEVEL_UP_WRONG_LEVEL",
      `Cannot level up from ${currentLevel} to ${targetLevel}. Levels must be sequential.`,
    )
  }

  const energyCost = HERO_LEVEL_UP_COSTS[targetLevel]
  if (seat.resources.currentEnergy < energyCost) {
    return err(
      "LEVEL_UP_INSUFFICIENT_ENERGY",
      `Leveling up to level ${targetLevel} costs ${energyCost} energy; you have ${seat.resources.currentEnergy}.`,
    )
  }

  const heroInst = state.instances[seat.hero.currentInstanceId]
  if (!heroInst)
    return err("LEVEL_UP_NO_HERO_INSTANCE", "Hero instance not found.")
  if (heroInst.status.exhausted) {
    return err(
      "LEVEL_UP_HERO_EXHAUSTED",
      "Hero is exhausted and cannot level up this turn.",
    )
  }

  const heroDef = heroes.get(seat.hero.heroId)
  if (!heroDef) {
    return err(
      "LEVEL_UP_HERO_DEF_NOT_FOUND",
      `Hero definition for ${seat.hero.heroId} not found.`,
    )
  }

  const newLevelDef = heroDef.progression[targetLevel - 1]
  const newLevel = targetLevel as 1 | 2 | 3

  let s = updateSeat(state, cmd.seatId, (seat) => ({
    ...seat,
    hero: { ...seat.hero, currentLevel: newLevel },
    resources: {
      ...seat.resources,
      currentEnergy: seat.resources.currentEnergy - energyCost,
    },
  }))

  // Update hero instance stats and combat state for new level
  s = updateInstance(s, heroInst.instanceId, (inst) => ({
    ...inst,
    currentAttack: newLevelDef.card.stats.attack,
    maxHealth: newLevelDef.card.stats.health,
    currentHealth: Math.min(inst.currentHealth, newLevelDef.card.stats.health),
    combatState: buildCombatStateForHeroLevel(heroDef, newLevel),
    status: { ...inst.status, exhausted: true, remainingAttack: 0 }, // leveling up exhausts the hero
  }))

  s = appendLog(s, cmd.seatId, "level_up_hero", {
    heroId: seat.hero.heroId,
    fromLevel: currentLevel,
    toLevel: targetLevel,
    energyCost,
  })

  return { ok: true, state: s }
}
